<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <div :class="dropdownStyles">
    <div
      @click="toggleDropdown"
      class="flex relative items-center justify-between sm:justify-end bg-primary py-2.5 px-3.5 2xl:py-3 2xl:px-5 z-20 cursor-pointer"
    >
      <label
        :class="[
          'font-bold mr-[10px] cursor-pointer',
          theme === 'dark' ? 'text-white-40' : 'text-aubergine-40',
        ]"
        >{{ label }}</label
      >
      <ArrowButton :isOpen="toggledState" />
    </div>
    <Transition
      enter-active-class="transition-all duration-500 in-expo"
      leave-active-class="transition-all duration-300 out-expo"
      enter-from-class="opacity-0 -translate-y-0"
      leave-to-class="opacity-0 -translate-y-0"
    >
      <div v-if="toggledState" :class="ulContainer">
        <ul :class="ulStyles">
          <li
            v-for="(option, index) in sortedOptions"
            :key="index"
            role="option"
            tabindex="0"
            :aria-selected="
              selectedOptionLabel === Object.keys(option)[0] ? 'true' : 'false'
            "
            @click="selectOption(option)"
            @keyup.enter.prevent="selectOption(option)"
            :class="[
              'font-bold text-aubergine px-[13px] py-[18px] inline-block hover:text-orange cursor-pointer',
              theme === 'dark' ? 'text-white' : 'text-aubergine',
              isNewsFilter ? '' : 'last:border-none',
              selectedArray?.includes(Object.keys(option)[0]) ? 'text-orange' : '',
              //selectedOptionLabel === Object.keys(option)[0] ? 'text-orange' : '',
            ]"
          >
            {{ Object.keys(option)[0] }}
          </li>
        </ul>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, toRefs, computed, watch } from "vue";
import ArrowButton from "@atoms/Dropdown/ArrowButton.vue";

export type Option = Record<string, string>;
interface DropdownProps {
  options: Option[];
  label: string;
  selectedOption: Option | null;
  selectedArray: string[] | null;
  theme: "dark" | "light";
  classes: string;
  isNewsFilter?: boolean;
  total?: number;
  isCategoryFilter?: boolean;
  toggledState: boolean;
}

type Emits = {
  (e: "selectFilterOption", option: Option): void;
  (e: "toggleState", isOpen: boolean): void;
};

const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<DropdownProps>(), {
  theme: "light",
});

const {
  options,
  label,
  selectedOption,
  selectedArray,
  theme,
  classes,
  isNewsFilter,
  toggledState,
} = toRefs(props);

const isOpen = ref(toggledState.value);

function toggleDropdown() {
  emit("toggleState", isOpen.value);
}

function selectOption(option: Option) {
  emit("selectFilterOption", option);
  //REMOVED TO KEEP DROPDOWN OPEN AFTER SELECTION
  //emit("toggleState", isOpen.value);
  //isOpen.value = false;
}

const selectedOptionLabel = computed(() => {
  if (!selectedOption.value) return;
  return Object.keys(selectedOption.value)[0];
});

const dropdownStyles = computed(() => {
  const isOpen = toggledState.value ? "z-[600]" : "";
  return `inline-flex justify-center flex-col relative bg-primary ${isOpen} ${classes.value} border-2 border-aubergine-40`;
});

const ulStyles = computed(() => {
  return `bg-primary inline-flex flex-col [&>*]:border-b-2 [&>*]:border-aubergine-40 w-full `;
});

const ulContainer = computed(() => {
  return `bg-primary inline-flex flex-col absolute top-full mt-[2px] left-0 lg:left-auto lg:right-0 ring ring-2 ring-primary outline outline-2 outline-aubergine-40 z-[500] max-h-[400px] w-full md:min-w-[350px] overflow-y-auto`;
});

const sortedOptions = computed(() => {
  const sorted = [...options.value].sort((a, b) => {
    const keyA = Object.keys(a)[0].toLowerCase();
    const keyB = Object.keys(b)[0].toLowerCase();
    return keyA.localeCompare(keyB);
  });

  // Ensure "All" is the first option, if it exists
  const allOptionIndex = sorted.findIndex(
    (option) => Object.keys(option)[0].toLowerCase() === "all"
  );
  if (allOptionIndex > -1) {
    const [allOption] = sorted.splice(allOptionIndex, 1);
    sorted.unshift(allOption);
  }

  return sorted;
});
</script>
